import { inject } from '@angular/core';
import { patchState, signalStore, withHooks, withMethods, withState } from '@ngrx/signals';
import { CustomerText } from '@raptor-fe/core';
import { ApplicationContextStore, SignalRMethods, SignalRService } from '@raptor-fe/services';

/**
 * A real-time customer-message delivery-status change pushed over SignalR. Carries only what a conversation
 * surface needs to advance the status indicator of a message it already holds -- not the whole message.
 */
export interface CustomerTextStatusUpdate {
    messageId: string;
    customerId?: string | null;
    statusId?: string | null;
    statusName?: string | null;
    failureReason?: string | null;
    statusUpdatedDate?: string | null;
}

/**
 * A real-time signal that a customer conversation gained a message that did not originate from the viewer's
 * own composer (for example a server-sent inspection report). Carries only the conversation identity so a
 * surface can tell whether the update concerns the conversation it is showing and re-pull it.
 */
export interface CustomerTextConversationUpdate {
    customerId: string;
}

interface CustomerTextRealtimeState {
    /** The most recent status update received; conversation surfaces react to it and patch their own thread. */
    lastStatusUpdate: CustomerTextStatusUpdate | null;
    /** The most recent conversation-updated signal; surfaces re-pull the thread for the named customer. */
    lastConversationUpdate: CustomerTextConversationUpdate | null;
    /**
     * The most recent message sent from any open conversation surface in this app instance. Relayed so a
     * second surface open on the same conversation (the popup and the Message Center at once) reflects the
     * send without a manual refresh -- SignalR only carries inbound and status, not the user's own outbound.
     */
    lastSentMessage: CustomerText | null;
}

/**
 * Root store that subscribes once to the SignalR delivery-status channel and exposes the latest update as a
 * signal. The conversation popup and the Message Center react to it and patch the matching message's status in
 * place, so an outbound message advances (queued -> submitted -> delivered / failed) without a manual refresh.
 * It only surfaces the signal; it never reloads a thread itself, keeping the store free of feature dependencies.
 */
export const CustomerTextRealtimeStore = signalStore(
    { providedIn: 'root' },
    withState<CustomerTextRealtimeState>({ lastStatusUpdate: null, lastConversationUpdate: null, lastSentMessage: null }),
    withMethods((store) => ({
        /** Relays a just-sent message so any other conversation surface open on the same customer reflects it. */
        publishSentMessage: (message: CustomerText): void => {
            patchState(store, { lastSentMessage: message });
        },
    })),
    withHooks((store, appContext = inject(ApplicationContextStore), signalRService = inject(SignalRService)) => ({
        onInit: () => {
            if (appContext.isLoggedIn() === false) {
                return;
            }

            signalRService.startConnection();
            signalRService.onNotification(SignalRMethods.CUSTOMER_TEXT_STATUS_UPDATED, async (data) => {
                patchState(store, { lastStatusUpdate: data as CustomerTextStatusUpdate });
            });
            signalRService.onNotification(SignalRMethods.CUSTOMER_TEXT_CONVERSATION_UPDATED, async (data) => {
                patchState(store, { lastConversationUpdate: data as CustomerTextConversationUpdate });
            });
        },
    })),
);
